// Firm intake state: one IntakeState from GET /api/intake, local edits, and autosave.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  REVIEW_STEP, STEP_FIELDS, normalizeToolRow, tipsFor, validateForSubmit,
  type AnswerStep, type FunctionId, type IntakeAnswers, type IntakeState, type Issue, type KickoffView,
  type SaveResponse, type ToolRow, type ToolSaveResponse, type ToolRowView,
} from '@upm/domain';
import { api } from '../lib/api';
import { Saver, savedLabel, type SaveStatus } from './saver';

type Ctx = {
  state: IntakeState;
  answers: IntakeAnswers;
  tools: Record<FunctionId, ToolRowView>;
  catalogLabels: Record<string, string>;
  issues: Issue[];               // live client-side validateForSubmit
  editable: boolean;
  submitted: boolean;
  saveStatus: SaveStatus;
  saveLabel: string;
  fatal: string | null;
  setAnswers: (step: AnswerStep, patch: Partial<IntakeAnswers>) => void;
  setToolRow: (fn: FunctionId, row: ToolRow, opts?: { typing?: boolean }) => void;
  touchStep: (step: number) => void;
  flush: () => Promise<boolean>;
  setKickoff: (k: KickoffView | null) => void;
  markSubmitted: () => void;
  reload: () => Promise<void>;
};

const IntakeCtx = createContext<Ctx | null>(null);
export const useIntake = () => {
  const c = useContext(IntakeCtx);
  if (!c) throw new Error('useIntake outside IntakeProvider');
  return c;
};

const pick = (a: IntakeAnswers, keys: readonly (keyof IntakeAnswers)[]) =>
  Object.fromEntries(keys.filter((k) => a[k] !== undefined).map((k) => [k, a[k]])) as Partial<IntakeAnswers>;
const rowKey = (r: ToolRow) => JSON.stringify([r.tools, r.other, r.notApplicable, r.usageNote]);
const SUBMITTED: IntakeState['status'][] = ['submitted', 'kickoff_booked', 'plan_drafted', 'plan_approved', 'handed_off'];

export function IntakeProvider({ initial, children }: { initial: IntakeState; children: ReactNode }) {
  const [state, setState] = useState(initial);
  const [answers, setAnswersState] = useState<IntakeAnswers>(initial.answers ?? {});
  const [tools, setTools] = useState(initial.tools);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [recent, setRecent] = useState(false);
  const [fatal, setFatal] = useState<string | null>(null);
  const [editable, setEditable] = useState(initial.editable);

  const answersRef = useRef(answers);
  const toolsRef = useRef(tools);
  answersRef.current = answers;
  toolsRef.current = tools;

  const saver = useMemo(() => new Saver((err) => {
    if (err.code === 'answers_locked') { setEditable(false); setFatal('Your answers are locked now that the Discovery Session has been held. Contact your Customer Experience Manager to change anything.'); }
    else if (err.code === 'unauthorized' || err.status === 401) setFatal('Your session has ended. Open the link in your invitation email again to keep going.');
    else setFatal(err.message || 'We couldn’t save your last change.');
  }), []);

  useEffect(() => {
    let t: ReturnType<typeof setTimeout> | undefined;
    const off = saver.subscribe((s) => {
      setSaveStatus(s);
      if (s === 'saved') { setRecent(true); clearTimeout(t); t = setTimeout(() => setRecent(false), 4000); }
    });
    const warn = (e: BeforeUnloadEvent) => { if (saver.pending) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => { off(); clearTimeout(t); window.removeEventListener('beforeunload', warn); saver.dispose(); };
  }, [saver]);

  const patchStep = useCallback(async (step: number) => {
    const keys = STEP_FIELDS[step as AnswerStep] ?? [];
    const res = await api.patch<SaveResponse>(`/api/intake/steps/${step}`, pick(answersRef.current, keys));
    setState((s) => ({ ...s, savedAt: res.savedAt, issues: res.issues, currentStep: Math.max(s.currentStep, step) }));
  }, []);

  const putTool = useCallback(async (fn: FunctionId) => {
    const row = toolsRef.current[fn];
    const sent = normalizeToolRow({ tools: row.tools, other: row.other, notApplicable: row.notApplicable, usageNote: row.usageNote });
    const sentKey = rowKey({ ...row });
    const res = await api.put<ToolSaveResponse>(`/api/intake/tools/${fn}`, sent);
    setState((s) => ({ ...s, savedAt: res.savedAt, issues: res.issues }));
    setTools((t) => (rowKey(t[fn]) === sentKey ? { ...t, [fn]: { ...t[fn], tips: res.row?.tips ?? t[fn].tips } } : t));
  }, []);

  const setAnswers = useCallback((step: AnswerStep, patch: Partial<IntakeAnswers>) => {
    const next = { ...answersRef.current, ...patch };
    answersRef.current = next;
    setAnswersState(next);
    saver.schedule(`step:${step}`, () => patchStep(step), 800);
  }, [saver, patchStep]);

  const setToolRow = useCallback((fn: FunctionId, row: ToolRow, opts?: { typing?: boolean }) => {
    const view: ToolRowView = { ...row, tips: tipsFor(fn, row) };
    const next = { ...toolsRef.current, [fn]: view };
    toolsRef.current = next;
    setTools(next);
    saver.schedule(`tool:${fn}`, () => putTool(fn), opts?.typing ? 800 : 0);
  }, [saver, putTool]);

  /** Records reaching a step that has no field edits (advances currentStep on the server). */
  const touchStep = useCallback((step: number) => {
    if (step >= 1 && step < REVIEW_STEP) saver.schedule(`step:${step}`, () => patchStep(step), 0);
  }, [saver, patchStep]);

  const reload = useCallback(async () => {
    await saver.flush();
    const s = await api.get<IntakeState>('/api/intake');
    setState(s);
    setAnswersState(s.answers ?? {});
    setTools(s.tools);
    setEditable(s.editable);
  }, [saver]);

  const setKickoff = useCallback((k: KickoffView | null) =>
    setState((s) => ({ ...s, kickoff: k, status: k?.status === 'booked' && s.status === 'submitted' ? 'kickoff_booked' : s.status })), []);
  const markSubmitted = useCallback(() => setState((s) => ({ ...s, status: SUBMITTED.includes(s.status) ? s.status : 'submitted' })), []);

  const issues = useMemo(() => validateForSubmit(answers, tools), [answers, tools]);
  const catalogLabels = useMemo(
    () => Object.fromEntries(Object.values(state.catalog).flat().map((o) => [o.id, o.label])),
    [state.catalog],
  );

  const value: Ctx = {
    state, answers, tools, catalogLabels, issues, editable,
    submitted: SUBMITTED.includes(state.status),
    saveStatus, saveLabel: savedLabel(saveStatus, recent), fatal,
    setAnswers, setToolRow, touchStep,
    flush: () => saver.flush(),
    setKickoff, markSubmitted,
    reload,
  };
  return <IntakeCtx.Provider value={value}>{children}</IntakeCtx.Provider>;
}
